<script>
export default {
  name: "CreateEditProgramSessions",
};
</script>

<script setup>
import { computed, reactive, ref, watch, unref, toRefs, toValue, onMounted } from "vue";
import { useStore } from "vuex";
import BaseToggle from "../../../../forms/SharedComponents/BaseToggle.vue";
import CreateEditProgramSessionsActions from "./CreateEditProgramSessionsActions.vue";
import Tooltip from "@/components/shared/Tooltip/PrimeVueTooltip.vue";
import SimpleBreadCrumb from "@/components/shared/SimpleBreadCrumb.vue";
import VSelectCaret from "@/components/shared/select/VSelectCaret.vue";
import BasicTextInput from "@/components/shared/input/BasicTextInput.vue";
import CheckboxSelect from "@/components/shared/select/CheckboxSelect.vue";
import SearchableSelect from "@/components/shared/select/SearchableSelect.vue";
import CalendarInput from "@/components/shared/CalendarInput.vue";
import BasicCheckbox from "@/components/shared/input/BasicCheckbox.vue";
import TagsInput from "@/components/shared/TagsInput.vue";
import BasicSwitch from "@/components/shared/input/BasicSwitch.vue";
import ExpandingSection from "@/components/shared/section/ExpandingSection.vue";
import ProgramLeaders from "@/components/program-manager/sessions/components/ProgramLeaders";
import ProgramLocationsSection from "@/components/program-manager/sessions/components/ProgramLocationsSection";
import ParticipantsSection from "@/components/program-manager/sessions/components/ParticipantsSection";
import TodosSection from "@/components/program-manager/sessions/components/TodosSection";
import { useSessionTodos } from "@/components/program-manager/sessions/components/TodosSection/composables/useSessionTodos";
import ProgramSessionFeesSummary from "@/components/program-manager/sessions/components/ProgramSessionFeesSummary";
import ProgramSessionOrdersSummary from "@/components/program-manager/sessions/components/ProgramSessionOrdersSummary";
import DisabledProgramSessionFeesSummary from "@/components/program-manager/sessions/components/ProgramSessionFeesSummary/DisabledProgramSessionFeesSummary.vue";
import ProductsAvailableAddOn from "@/components/program-manager/sessions/components/ProductsAvailableAddOn";
import ExcursionsAndEvents from "@/components/program-manager/sessions/components/ExcursionsAndEvents";
import Internships from "@/components/program-manager/sessions/components/Internships";
import Housing from "@/components/program-manager/sessions/components/Housing";
import AdditionalProgramSessionFees from "@/components/program-manager/sessions/components/AdditionalProgramSessionFees";
import PromotionalSettings from "@/components/program-manager/sessions/components/PromotionalSettings";
import Academics from "@/components/program-manager/sessions/components/Academics";
import DetailModal from "@/components/modals/DetailModal";
import ArchivingModal from "@/components/modals/ArchivingModal.vue";
import DuplicateSessionModal from "@/components/modals/duplicate/ProgramSession.vue";
import { helpers, required, requiredIf, numeric, minValue } from "@vuelidate/validators";
import useVuelidate from "@vuelidate/core";
import { cloneDeep, debounce } from "lodash";
import RequiredToSavePublish from "@/components/program-manager/housing/components/RequiredToSavePublish";
import { useToast } from "vue-toast-notification";
import {
  vuelidateCollectionHelper,
  convertProgramSession,
  convertProgramSessionBeforeSave,
  getSessionPricingSummary,
} from "@/components/program-manager/sessions/components/CreateEditProgramSessions/utils";
import { resolveFinancialProductDefaults } from "@/components/program-manager/sessions/components/CreateEditProgramSessions/financialProductDefaults";
import { differenceInDays, format, parse } from "date-fns";
import {
  channelTypes,
  channelTypesMapper,
  channelTypeOptions,
  sessionStatus,
  sessionStatuses,
  sessionTypes as sessionTypesList,
  sessionType,
  DURATION_TOOLTIP_MESSAGE,
  PRICE_DURATION_DISABLED_STATUS,
  exchangeSessionType,
} from "@/components/program-manager/sessions/constants";
import {
  debounceSearchWrapper,
  useGetEntities,
  useGetProgramSessionsOptions,
  useGetPrograms,
  useAcademicYearFilter,
} from "@/components/program-manager/sessions/composable";
import {
  canEditSessionConfigurableFees,
  hasConfigurableRegistrationEnabled,
  isTenantManager,
  hasMyStudentsToDosEnabled,
  useCustomPriceFees,
  isTenantLibrariesFlagEnabled,
  hasEnableShareOptionsEnabled,
  hasGlobalLibrariesEnabled,
} from "@/composables/authorization";
import { useRouter } from "vue-router";
import { verifyStartDateLowerThenEndDate } from "@/util/date";
import { getEntityById, parseErrorResponse } from "@/services/utils.js";
import {
  API_ENTITY_INFORMATION,
  ERROR_TIMEOUT,
  DEFAULT_TENANT_FINANCIAL_TYPE,
  DEFAULT_TENANT_FINANCIAL_LINE,
  ORDER_STATUS,
  PERMISSIONS,
} from "@/constants";
import { useQuery, useQueryClient } from "@tanstack/vue-query";
import programSessionsService from "@/services/programSessions";
import ConfirmOwnerModal from "@/components/modals/ConfirmOwnerModal/ConfirmOwnerModal";
import { CONFIRM_OWNER_MODAL_ACTIONS } from "@/components/modals/ConfirmOwnerModal/confirmOwnerModalActions";
import {
  useHasAccessToMultiTenancy,
  useLibraryItemOwnerSelectorEnabled,
  useMultiTenantEntityParams,
  useDefaultCurrentSelectedTenantOwnerId,
} from "@/composables/useMultiTenancy";
import { useSessionFees } from "@/composables/useSessionFees";
import { centsToDollars } from "@/mixins/helpers";
import { REGISTRATION_TYPE } from "@/components/program-manager/sessions/constants";
import { useGetFinancialTypesData } from "@/providers/financialTypes/useGetFinancialTypesData";
import { useGetFinancialLinesData } from "@/providers/financialTypes/useGetFinancialLinesData";
import ProgramSessionSpecificAdvisingInstructions from "@/components/program-manager/sessions/components/ProgramSessionSpecificAdvisingInstructions";

const store = useStore();
const router = useRouter();
const toast = useToast();
const queryClient = useQueryClient();

const props = defineProps({
  programSessionId: {
    type: String,
    required: false,
  },
  channelId: {
    type: [String, Number],
    required: false,
  },
  programSessionOrig: {
    type: Object,
    required: false,
  },
  tags: {
    type: Object,
    required: true,
  },
});

const confirmOwnerOpen = ref(false);

const SESSION_FORM_MODE = {EDIT: "EDIT", CREATE: "CREATE"}

// How the page was entered. Fixed for the lifetime of the component: creating a session
// swaps the route param for its new id without remounting, so this must not be treated as
// a live view of whether the session has been persisted yet.
const sessionFormMode = props.programSessionOrig ? SESSION_FORM_MODE.EDIT : SESSION_FORM_MODE.CREATE;

const onConfirmOwnerClose = () => {
  confirmOwnerOpen.value = false;
};

const programOrSession = ref("session");
const ownerName = ref("");
const newOwner = ref("");

const entityIdFilterParams = computed(() => toValue(useMultiTenantEntityParams()));

const originalSessionOwner = ref({});
const selectedProgramSessionOwner = ref(undefined);

// Tenancy related: Fields will not be shown if it's not owned by API
const ownerIsApi = computed(() => programSession.value?.owner_entity_id === API_ENTITY_INFORMATION.id);

const programSessionEntityId = computed(() => {
  return isFeatureTenantLibraries.value ? selectedProgramSessionOwner.value?.id : ownerEntityId.value;
});

watch(
  () => props.programSessionOrig,
  async (newVal) => {
    if (newVal) {
      const originalSessionOwnerId = toValue(newVal)?.owner_entity_id;
      if(Boolean(originalSessionOwnerId)){
        originalSessionOwner.value = await getEntityById(originalSessionOwnerId);
      }
    }
  },
  { immediate: true }
);

const isOwnerDifferent = computed(() => {
  const originalSessionOwnerId = toValue(props.programSessionOrig)?.owner_entity_id;

  // Only a move between two known owners is a change worth confirming. A session created in
  // place never receives programSessionOrig, so without this it reads as "owner changed" on
  // every save after the first, and the modal's keep-original branch would then write back
  // an undefined owner_entity_id.
  if (!originalSessionOwnerId) {
    return false;
  }

  return originalSessionOwnerId !== selectedProgramSessionOwner.value?.id;
});

const shouldShowSessionOwnerSelector = computed(() => toValue(useLibraryItemOwnerSelectorEnabled()));

const { options: academicYearOptions } = useAcademicYearFilter({
  idIsNotIndex: true,
});

const config = {
  dateFormat: "m/d/Y",
  parseDate: (dateStr) => {
    return parse(dateStr, "yyyy-MM-dd", new Date(), {});
  },
};

let terms_and_dates_disabled = ref(false);

const { programSessionId, channelId } = toRefs(props);

// Pass the ref, not a snapshot: creating a session swaps the route param for its new id, and a
// plain string would pin the fee query to the channel placeholder for the component's lifetime.
const { sessionApplicationFee, sessionConfirmationDeposit } = useSessionFees(programSessionId);

const selectedEntityView = ref(store.getters["programManager/getSelectedEntityView"]);

const channelIdCheck = computed(() => {
  return programSessionId.value == channelId.value;
});

const isStatusAllowed = (status) => {
  return true;
};

const specificCategoryTags = computed(() => {
  return props.tags.getSpecificCategoryTags()("Calendar");
});

const getTermsData = computed(() => store.getters["programManager/getTermsData"]);

// TODO: I dont like doing this. This probably should be in a computed. If I use a computed the vuelidate breaks,
// it can't seem to handle the programSession object changing. Come back later, move this to computed and maybe try
// to re initialize the vuelidated if the programs sessions ever changes.
let programSession = ref();

// Own the to-dos data layer here (the orchestrator) so the working copy can be
// injected into the session save payload — see the save flow comment for why
// the backend's relationship-managed PUT requires session_todos on the payload.
// The same working copy is passed down to the presentational child via v-model.
const { sessionTodos, sessionTodosLoading, sessionTodosSettled, sessionTodosHydrated } = useSessionTodos(
  computed(() => programSession.value?.id),
  { enabled: hasMyStudentsToDosEnabled },
);

const ownerEntityId = computed(() => {
  const entities = store.getters.getEntityIds;
  return entities?.map((id) => id)[0];
});

const ownerEntityListNonTenantManager = computed(() => {
  return store.getters.getEntityIds;
});

const isSessionOwner = computed(() => {
  const sessionOwnerEntityId = props.programSessionOrig?.owner_entity_id;
  if (!sessionOwnerEntityId) return true;
  return ownerEntityListNonTenantManager.value.includes(sessionOwnerEntityId) || isTenantManager();
});

const permissions = computed(() => store.getters.getPermissions);


const disablePriceDuration = computed(
  () =>
    !permissions.value.includes(PERMISSIONS.PRGMS_PUB) &&
    PRICE_DURATION_DISABLED_STATUS.includes(programSession.value.status_id)
);

const hasProgramSessionId = computed(() => {
  return programSession.value.id || null;
});

const todoSessionsCanEdit = computed(() => {
  return (
    permissions.value.includes(PERMISSIONS.SUPER_ADMIN) ||
    permissions.value.includes(PERMISSIONS.PRGMS_PUB) ||
    (programSession.value.status_id === sessionStatus.draft && permissions.value.includes(PERMISSIONS.PRGMS_WRITE))
  );
});

const canEditConfigurableFees = computed(() =>
  canEditSessionConfigurableFees({
    isSessionOwner: isSessionOwner.value,
    sessionStatusId: programSession.value?.status_id,
    draftStatusId: sessionStatus.draft,
  }),
);

const sessionPricingSummaryForFees = computed(() => {
  const ps = programSession.value;
  if (!ps) {
    return getSessionPricingSummary({}, sessionApplicationFee.dollars, sessionConfirmationDeposit.dollars);
  }
  if (useCustomPriceFees.value) {
    const appCents = ps?.application_fee_in_cents;
    const confCents = ps?.confirmation_deposit_in_cents;
    const appDollars =
      typeof appCents === "number" && appCents > 0 ? centsToDollars(appCents) : 0;
    const confDollars =
      typeof confCents === "number" && confCents > 0 ? centsToDollars(confCents) : 0;
    return getSessionPricingSummary(ps, appDollars, confDollars);
  }
  return getSessionPricingSummary(ps, sessionApplicationFee.dollars, sessionConfirmationDeposit.dollars);
});

const financialTypesRef = ref("");
const { data: financialTypesData, isLoading: isLoadingFinancialType } = useGetFinancialTypesData({limit: 100, page: 1, q: financialTypesRef})

const financialTypesOptions = computed(() => {
  return Array.isArray(financialTypesData.value)
    ? financialTypesData.value.filter((opt) => opt?.id !== DEFAULT_TENANT_FINANCIAL_TYPE.id)
    : [];
});

const financialLinesRef = ref("");
const { data: financialLinesData, isLoading: isLoadingFinancialLine } = useGetFinancialLinesData({limit: 100, page: 1, q: financialLinesRef})

const financialLineOptions = computed(() => {
  return Array.isArray(financialLinesData.value)
    ? financialLinesData.value.filter((opt) => opt?.id !== DEFAULT_TENANT_FINANCIAL_LINE.id)
    : [];
});

const { data: sessionSummaryData } = !channelIdCheck.value
  ? useQuery({
      queryKey: ["sessionSummary", programSessionId],
      queryFn: () =>
        programSessionsService.getProgramSessionSummary({
          id: toValue(programSessionId),
          status: [ORDER_STATUS.draft, ORDER_STATUS.submitted, ORDER_STATUS.paidConfirmed],
        }),
    })
  : { isLoading: undefined, data: undefined };

const { execute: executeGetEntities, state: entitiesOptions, isLoading: entitiesOptionsLoading } = useGetEntities(
  { immediate: true, throwError: false, resetOnExecute: true },
  {
    extraParams: {
      entity_ids: toValue(entityIdFilterParams),
    },
  }
);

// Added second entities call to isolate data from owner dropdown
const {
  execute: executeGetAllEntities,
  state: entitiesOptionsAll,
  isLoading: entitiesOptionsAllLoading,
} = useGetEntities({
  immediate: true,
  throwError: false,
  resetOnExecute: true,
});

const fetchEntitiesOptions = debounceSearchWrapper(executeGetEntities, 250);

const fetchEntitiesOptionsAll = debounceSearchWrapper(executeGetAllEntities, 250);

const isFeatureTenantLibraries = computed(() => {
  const featureFlags = store.state.featureFlags;
  return featureFlags["tenant-libraries"];
});

const isFeatureSessionEditMenu = computed(() => {
  const featureFlags = store.state.featureFlags;
  return featureFlags["session-edit-menu"];
});

watch(programSessionId, (newId) => {
  store.dispatch("programSessions/fetchProgramSessionData", {
    programSessionId: newId,
    skipCache: true,
  });
});

watch(
  [() => props.channelId, () => props.programSessionOrig],
  async ([newChannelId, newProgramSessionOrig]) => {
    if (newChannelId && !newProgramSessionOrig?.id) {
      programSession.value = convertProgramSession({
        channel_id: newChannelId,
        owner_entity_id: toValue(useDefaultCurrentSelectedTenantOwnerId()),
        is_gpa_required: true,
      });
    } else {
      programSession.value = convertProgramSession(cloneDeep(newProgramSessionOrig));
    }

    if (isFeatureTenantLibraries.value) {
      if (hasProgramSessionId.value && programSession.value.owner_entity_id) {
        selectedProgramSessionOwner.value = await getEntityById(programSession.value.owner_entity_id);
      } else if (selectedEntityView.value) {
        selectedProgramSessionOwner.value = selectedEntityView.value;
      } else {
        toast.open({
          message: "No entity id associated with user",
          type: "error",
          position: "bottom",
          duration: ERROR_TIMEOUT,
        });
      }
    }
  },
  { immediate: true }
);

const calculatedAcademicDuration = computed(() =>
  !!programSession.value?.academic_end_date && !!programSession.value?.academic_start_date
    ? differenceInDays(
        new Date(programSession.value.academic_end_date),
        new Date(programSession.value.academic_start_date)
      )
    : programSession.value?.calculated_academic_duration
);

watch(ownerIsApi, (newApiOwner) => {
  const patch = resolveFinancialProductDefaults({
    isApiOwner: newApiOwner,
    hasSessionId: Boolean(programSession.value?.id),
  });
  // undefined patch = existing API session; leave its saved values untouched.
  if (patch) {
    programSession.value.financial_product_line_id = patch.financial_product_line_id;
    programSession.value.financial_product_type_id = patch.financial_product_type_id;
  }
}, { immediate: true });

watch(
  () => programSession.value?.session_types,
  (newSessionType, oldSessionType) => {
    const newIncludesStudy = newSessionType.find((val) => val.id == sessionType.study);
    if (!newIncludesStudy && programSession.value?.host_institution_program_name) {
      programSession.value.host_institution_program_name = undefined;
    }

    const newIncludesExchange = newSessionType.find((val) => val.id == sessionType.exchange);
    if (!newIncludesExchange && programSession.value?.host_institution_program_name) {
      programSession.value.host_institution_program_name = undefined;
    }

    const newIncludesStudyFacultLed = newSessionType.find(
      (val) => val.id === sessionType.study || val.id === sessionType.faculty_led
    );
    if (!newIncludesStudyFacultLed) {
      programSession.value.sponsoring_institutions.entity = undefined;
    }
  }
);

watch(
  () => programSession.value.channel_id,
  (newValueChannelId, oldValueChannelId) => {
    if (newValueChannelId != channelTypes.CUSTOM) {
      programSession.value.sponsoring_institutions.entity = undefined;
    }
  }
);

const sessionTypes = computed(() => {
  if (programSession?.value?.channel_id === channelTypes.EXCHANGE) {
    return [exchangeSessionType, ...sessionTypesList];
  } else {
    const idx = programSession?.value?.session_types?.findIndex((obj) => obj.id === 10);
    if (idx !== -1) {
      programSession?.value?.session_types?.splice(idx, 1);
    }
    return [...sessionTypesList];
  }
});

//TODO: This is just an example of how to use the VUE suspense. It will be removed later
// const programSession = await store.dispatch("programSessions/fetchProgramSessionData", {id: props.programSessionId})

// const programSession = reactive(
//   cloneDeep(store.getters["programSessions/getProgramSession"])
// );

const typeStudy = computed(() => !!programSession.value?.session_types?.find((val) => val.id == sessionType.study));

const typeFacultyLed = computed(
  () => !!programSession.value?.session_types?.find((val) => val.id === sessionType.faculty_led)
);

const sessionTypeCoreStudy = computed(
  () =>
    typeStudy.value &&
    (programSession.value.channel_id === channelTypes.CORE ||
      programSession.value.channel_id === `${channelTypes.CORE}`)
);

const sessionTypeCustomStudy = computed(
  () =>
    typeStudy.value &&
    (programSession.value.channel_id === channelTypes.CUSTOM ||
      programSession.value.channel_id === `${channelTypes.CUSTOM}`)
);

const sessionTypeCustomFacultyLed = computed(
  () =>
    typeFacultyLed.value &&
    (programSession.value.channel_id === channelTypes.CUSTOM ||
      programSession.value.channel_id === `${channelTypes.CUSTOM}`)
);

const sessionTypeAdoptedCustom = computed(
  () =>
    programSession.value.channel_id === channelTypes.ADOPTED ||
    programSession.value.channel_id === channelTypes.CUSTOM ||
    programSession.value.channel_id === `${channelTypes.ADOPTED}` ||
    programSession.value.channel_id === `${channelTypes.CUSTOM}`
);

const sessionTypeExchange = computed(() => programSession.value.channel_id == channelTypes.EXCHANGE);

const sessionEnableProductLineId = computed(() => !!programSession?.value?.session_hosting_institutions[0]?.entity);

const requiredToEnroll = helpers.withMessage(
  "This field is required for Open Enrollment",
  requiredIf(function () {
    return programSession?.value?.status_id === sessionStatus.openEnrollment;
  })
);

const currentChannelType = computed(() => {
  return channelTypesMapper[programSession.value.channel_id];
});

const requiredForAdoptedCustom = helpers.withMessage(
  () => `This field is required for session of type ${currentChannelType.value}`,
  requiredIf(() => sessionTypeAdoptedCustom.value)
);

const checkStartEndDates = (value, { academic_start_date: startDate, academic_end_date: endDate } = {}) =>
  unref(programSession)?.status_id === sessionStatus.draft
    ? true
    : verifyStartDateLowerThenEndDate(startDate, endDate);

const rules = computed(() => ({
  programSession: {
    name: {
      required: helpers.withMessage("Program Session Name is required", required),
    },
    salesforce_id: {
      required: helpers.withMessage("Salesforce Program Session ID is required", requiredToEnroll),
    },
    session_types: {
      required: requiredToEnroll,
    },
    sponsoring_institutions: {
      entity: {
        required: helpers.withMessage(
          "Sponsoring Institution is required",
          requiredIf(function () {
            return sessionTypeCustomStudy?.value || sessionTypeCustomFacultyLed?.value;
          })
        ),
      },
    },
    session_hosting_institutions: {
      $each: helpers.forEach({
        entity: {
          required: helpers.withMessage(
            "Host Institution is required to Publish",
            requiredIf(function () {
              return programSession?.value?.status_id === sessionStatus.openEnrollment && sessionTypeCoreStudy?.value;
            })
          ),
        },
      }),
    },
    session_profiles: {
      $each: helpers.forEach({
        profile_id: {
          required: helpers.withMessage(
            "Program leader name is required to Publish",
            requiredIf(function () {
              return programSession?.value?.status_id === sessionStatus.openEnrollment && sessionTypeCoreStudy?.value;
            })
          ),
        },
      }),
    },

    academic_start_date: {
      required: requiredToEnroll,
      checkStartEndDates: helpers.withMessage("Start date should be before end date", checkStartEndDates),
    },
    academic_end_date: {
      required: requiredToEnroll,
      checkStartEndDates: helpers.withMessage("Start date should be before end date", checkStartEndDates),
    },
    duration: {
      required: requiredToEnroll,
      numeric,
      minValueValue: minValue(1),
    },
    application_deadline: {
      required: requiredToEnroll,
    },
    enrollment_deadline: {
      required: helpers.withMessage(
        "This field is required for Open Enrollment",
        requiredIf(() => programSession?.value?.status_id === sessionStatus.openEnrollment && !hasMyStudentsToDosEnabled.value)
      )
    },
    // TODO: Check with product if and when this should be activated
    // final_payment_deadline: {
    //   required: requiredForAdoptedCustom,
    // },
    parent_id: {},
    academic_year: {
      required: requiredToEnroll,
    },
    terms: {
      required: requiredToEnroll,
    },
    status_id: {},
    academic_start_date_finalized: {},
    academic_end_date_finalized: {},
    visa_required: {},
    designated_insurance: {},
    group_flight: {},
    public_transportation_card: {},
    host_institution_program_name: {},
    financial_product_type_id: { required: requiredToEnroll },
    financial_product_line_id: { required: requiredToEnroll },
  },
}));

// const updateSessionHostInstitution = (value) => {
//   if (value) {
//     programSession.value.session_hosting_institutions[0] = {
//       ...programSession.value?.session_hosting_institutions[0],
//       entity_id: value?.id,
//       name: value?.name,
//       product_line_id: undefined,
//     };
//   } else {
//     programSession.value.session_hosting_institutions[0] = undefined;
//   }
// };

const { state: programsStateOptions } = useGetPrograms(
  { immediate: !!programSessionId?.value },
  {
    extraParams: {
      session_ids: programSessionId?.value && !channelIdCheck.value ? [programSessionId.value] : undefined,
      limit: 1,
    },
  }
);

const detailModalState = ref({
  id: undefined,
  state: false,
});

const duplicateModalState = ref({
  id: undefined,
  state: false,
});

const openDetailModal = () => {
  detailModalState.value = { id: props.programSessionId, state: true };
};

const openDuplicateModal = () => {
  duplicateModalState.value = { id: props.programSessionId, state: true };
};

const closeDuplicateModal = () => {
  duplicateModalState.value = { id: undefined, state: false };
};

const closeDetailModal = () => {
  detailModalState.value = { id: undefined, state: false };
};

const disablePreviewButton = computed(() => !(programsStateOptions?.value?.count > 0));

const disableTermsAndDates = (val) => {
  terms_and_dates_disabled.value = val;
  if (val) {
    programSession.value.academic_year = undefined;
    programSession.value.terms = [];
  }
};

const changeSessionStatus = async () => {
  v$.value.$reset();
  await v$.value.$validate();
};

const {
  execute: executeFetchParentOptions,
  state: parentOptions,
  isLoading: parentOptionsLoading,
} = useGetProgramSessionsOptions({ immediate: false }, {});

//TODO: Add a helper function _.partial/curry to combine this common reused code block
const fetchParentOptions = debounce(async (search, loading) => {
  if (search) {
    loading(true);
    try {
      await executeFetchParentOptions(0, {
        search,
        programSessionId: props.programSessionId,
      });
    } finally {
      loading(false);
    }
  }
}, 250);

let state = reactive({
  programSession,
});

const onSaveOrUpdateOwner = async (action) => {
  if (action === CONFIRM_OWNER_MODAL_ACTIONS.save) {
    programSession.value.owner_entity_id = originalSessionOwner.value.id;
    await saveProgramSession();
    selectedProgramSessionOwner.value = originalSessionOwner.value;
  } else if (action === CONFIRM_OWNER_MODAL_ACTIONS.update) {
    programSession.value.owner_entity_id = programSessionEntityId.value;
    await saveProgramSession();
    originalSessionOwner.value = selectedProgramSessionOwner.value;
  }

  confirmOwnerOpen.value = false;
};

const saveOrUpdate = async () => {
  const shouldShow = toValue(shouldShowConfirmOwnerModal);
  const isDifferent = toValue(isOwnerDifferent);

  if (isDifferent && shouldShow) {
    ownerName.value = originalSessionOwner.value?.name;
    newOwner.value = selectedProgramSessionOwner.value?.name;
    confirmOwnerOpen.value = true;
  } else {
    await saveProgramSession();
  }
};

const saveProgramSession = async () => {
  v$.value.$reset();
  const isValid = await v$.value.$validate();

  if (isValid) {
    const isNotExternalListing = programSession.value.registration_type !== REGISTRATION_TYPE.externalListing;
    if (!programSession.value.is_external_link_required) {
      programSession.value.external_link_instructions = undefined;
      if (isNotExternalListing) {
        programSession.value.external_link_url = undefined;
        programSession.value.external_link_site_name = undefined;
      }
    }
    if (hasConfigurableRegistrationEnabled.value) {
      if (!isNotExternalListing) {
        programSession.value.external_link_instructions = undefined;
      }
      if (!programSession.value.requires_application && isNotExternalListing) {
        programSession.value.prerequisites = undefined;
        programSession.value.corequisities = undefined;
        programSession.value.difficulty_score = undefined;
        programSession.value.acceptance_decisions = [];
      }
      if (!programSession.value.is_gpa_required) {
        programSession.value.gpa_exceptions_allowed = false;
        programSession.value.minimum_gpa_requirement = undefined;
        programSession.value.gpa_exception_requirements = [];
      }
    }
    try {
      const sessionProfilesBackup = cloneDeep(programSession.value.session_profiles);

      // Omit session_todos unless the working copy actually came back from the server. The
      // backend archives rows missing from the payload, so sending the empty copy left behind
      // by a failed read would wipe the session's to-dos; leaving the key off entirely is how
      // the flag-off path already avoids touching them.
      const includeSessionTodos = hasMyStudentsToDosEnabled.value && sessionTodosHydrated.value;

      const updatedSession = {
        ...convertProgramSessionBeforeSave(programSession.value),
        ...(includeSessionTodos && { session_todos: sessionTodos.value }),
      };
      const updatedProgramSession = await store.dispatch("programSessions/updateProgramSessions", {
        updatedSession,
      });
      if (updatedProgramSession) {
        programSession.value = convertProgramSession(
          cloneDeep({
            ...updatedProgramSession,
            session_profiles: sessionProfilesBackup,
          })
        );

        state = reactive({
          programSession: updatedProgramSession,
        });

        if (props.channelId) {
          await router.replace({
            name: "edit-program-sessions",
            params: { programSessionId: updatedProgramSession.id },
          });
        }

        // The session (including its to-do changes) is persisted regardless of
        // the NetSuite sync result, so always refresh the to-dos view.
        queryClient.invalidateQueries({ queryKey: ["sessionTodos", updatedProgramSession.id] });

        if(updatedProgramSession.netsuite_error) {
          const detail = updatedProgramSession.netsuite_error.split(' ').slice(1).join(' ');
          toast.open({
            message: `Program Session saved. NetSuite sync failed: ${detail}`,
            type: "warning",
            position: "bottom",
            duration: ERROR_TIMEOUT * 2,
          });
        } else if (hasMyStudentsToDosEnabled.value && !includeSessionTodos) {
          // Say so rather than reporting a clean save: the to-do section on screen is empty
          // because the read failed, and any edits made to it were not part of this save.
          toast.open({
            message: "Program Session saved. To-dos could not be loaded, so they were left unchanged.",
            type: "warning",
            position: "bottom",
            duration: ERROR_TIMEOUT * 2,
          });
        } else {
          toast.open({
            message: "Program Session Saved Successfully",
            type: "success",
            position: "bottom",
            duration: 5000,
          });
        }

        // For some reason this needs to be reset again
        // in order to not be considered dirty.
        v$.value.$reset();
      }
    } catch (e) {
      let cause = parseErrorResponse(e?.cause || "");
      cause = cause ? `: ${cause}` : "";
      toast.open({
        message: `Failed to save Program Session${cause}`,
        type: "error",
        position: "bottom",
        duration: ERROR_TIMEOUT,
      });
    }
  } else {
    toast.open({
      message: "Please fix validation errors before saving",
      type: "info",
      position: "bottom",
      duration: ERROR_TIMEOUT,
    });
  }
};

const v$ = useVuelidate(rules, state, {
  $registerAs: "CreateEditSession",
  $lazy: true,
});

const anyDirty = computed(() => {
  return v$.value.$anyDirty;
});

const openArchive = () => {
  store.dispatch("programManager/setArchivingModalStateOpen", {
    item: {
      id: programSessionId.value,
      "Program Session Name": programSession.value?.name,
    },
  });
};

const shouldShowConfirmOwnerModal = computed(() => {
  const featureFlagEnabled = toValue(isTenantLibrariesFlagEnabled);
  const hasAccessToMultiTenancy = toValue(useHasAccessToMultiTenancy);
  const programSessionExists = toValue(programSession)?.created_at;

  return Boolean(featureFlagEnabled && hasAccessToMultiTenancy && programSessionExists);
});

const updateSessionOwnerEntity = (selectedProgramSessionOwner) => {
  const ownerId = selectedProgramSessionOwner?.id;
  const ownerOptions = toValue(entitiesOptions)?.items || [];

  if (ownerId && Array.isArray(ownerOptions) && ownerOptions.length > 0) {
    const ownerEntity = ownerOptions.find((item) => item.id === ownerId);
    selectedProgramSessionOwner.value = ownerEntity;
    programSession.value.owner_entity_id = ownerEntity.id;
  }
};

// Saving mid-flight would send a half-built to-do list, so the button waits for the query to
// settle — and only that. Deriving this from the query rather than from how the page was entered
// keeps it correct after a session is created in place, where programSessionOrig stays undefined
// because the route param changes without a remount. A failed fetch counts as settled: the
// payload drops session_todos in that case (see saveProgramSession), so the form stays usable
// without risking the existing rows.
const disabledButtonLoading = computed(() => !sessionTodosSettled.value);

onMounted(() => {
  if (sessionFormMode === SESSION_FORM_MODE.CREATE && Number(props.channelId) === channelTypes.CUSTOM) {
    programSession.value.hide_from_search = true;
  };
});
</script>

<template>
  <div v-if="programSession" class="p-4 border-b border-gray-200 mb-24">
    <ArchivingModal />
    <DetailModal :data="detailModalState" @close-modal="closeDetailModal" />
    <DuplicateSessionModal :data="duplicateModalState" @close-modal="closeDuplicateModal" />
    <div class="flex justify-between mb-1 mt-5">
      <SimpleBreadCrumb />
      <div v-if="shouldShowConfirmOwnerModal">
        <ConfirmOwnerModal
          :open="confirmOwnerOpen"
          :programOrSession="programOrSession"
          :currentOwner="ownerName"
          :newOwner="newOwner"
          @on-cancel="onConfirmOwnerClose"
          @onSaveOrUpdateOwner="onSaveOrUpdateOwner"
        />
      </div>
      <div class="flex text-sm">
        <h3 class="font-bold mr-1">Program-Session ID:</h3>
        <span>{{ programSession.id }}</span>
      </div>
    </div>
    <div class="my-7 sm:flex sm:items-center sm:justify-between">
      <div class="w-full flex">
        <span class="text-red-100 pr-4 align-top"> * </span>
        <div class="flex flex-grow-1 w-full">
          <input
            id="name"
            v-model="programSession.name"
            type="text"
            name="name"
            :class="[
              {
                'bg-error-100': v$.programSession.name.$errors?.length,
              },
              'w-full text-2xl font-medium leading-6 text-gray-900 bg-blue-100 focus:outline-none min-w-[300px]',
            ]"
            @blur="v$.programSession.name.$touch"
          />
          <div v-if="v$.programSession.name.$errors.length" class="common-error-text">
            <span v-for="(error, idx) in v$.programSession.name.$errors" :key="idx">
              {{ error.$message }}
            </span>
          </div>
          <img
            v-if="(hasEnableShareOptionsEnabled || hasGlobalLibrariesEnabled) && programSession.marketplace"
            src="/images/icon-marketplace.svg"
            class="h-5 mt-1"
            data-testid="globe-icon"
          />
        </div>
      </div>
      <div class="w-full flex items-center space-x-4 mt-3 sm:mt-0 sm:ml-4">
        <template v-if="shouldShowSessionOwnerSelector">
          <label v-if="hasProgramSessionId" class="block text-sm font-semibold text-gray-700">
            Owner
          </label>
          <div class="mt-1 block w-96">
            <SearchableSelect
              v-model="selectedProgramSessionOwner"
              :clear-search-on-blur="() => false"
              :deselect-from-dropdown="true"
              :loading="entitiesOptionsLoading"
              :options="entitiesOptions.items"
              placeholder="Select Owner"
              label="name"
              @search="fetchEntitiesOptions"
              @update:modelValue="updateSessionOwnerEntity"
            >
            </SearchableSelect>
          </div>
        </template>
        <label class="common-label-text"><span class="text-red-100 pr-2">*</span>Status</label>
        <div class="mt-1 block w-56">
          <v-select-caret
            v-model="programSession.status_id"
            :options="sessionStatuses"
            :selectable="isStatusAllowed"
            :reduce="(val) => val.id"
            label="name"
            :vuelidate-instance="v$.programSession.status_id"
            :clearable="false"
            @update:modelValue="changeSessionStatus"
          >
          </v-select-caret>
        </div>
        <button
          v-if="!isFeatureSessionEditMenu"
          :disabled="disablePreviewButton"
          type="button"
          class="common-green-btn"
          @click="openDetailModal"
        >
          Preview
        </button>
        <button
          v-if="!isFeatureSessionEditMenu"
          type="button"
          class="common-green-btn"
          :disabled="disabledButtonLoading"
          @click="saveOrUpdate"
        >
          Save
        </button>
        <button
          v-else
          type="button"
          class="common-green-btn !rounded-none"
          :disabled="disabledButtonLoading"
          @click="saveOrUpdate"
        >
          Save
        </button>
        <button
          v-if="!isFeatureSessionEditMenu"
          type="button"
          class="common-green-btn"
          :disabled="disabledButtonLoading"
          @click="openArchive"
        >
          ARCHIVE
        </button>
        <CreateEditProgramSessionsActions
          v-if="isFeatureSessionEditMenu"
          :id="programSession.id"
          :entityId="programSessionEntityId"
          :name="programSession.name"
          @open-detail-modal="openDetailModal"
          @open-duplicate-modal="openDuplicateModal"
        />
      </div>
    </div>
    <div class="flex justify-between items-center">
      <h1 class="text-xl font-medium leading-6 text-gray-900">
        General Info
      </h1>
      <RequiredToSavePublish />
    </div>
    <div class="grid grid-cols-5 gap-x-12">
      <div class="col-span-3">
        <div class="grid grid-cols-2 gap-x-24 gap-y-4">
          <BasicTextInput
            v-model="programSession.salesforce_id"
            outer-classes="mt-4"
            input-classes="h-10 w-full form-input"
            :vuelidate-instance="v$.programSession.salesforce_id"
          >
            <template #fieldLabel>
              <label class="common-label-text">
                <span class="text-red-100 pr-2">**</span>
                Salesforce Program Session ID
              </label>
            </template>
          </BasicTextInput>
          <v-select-caret
            v-model="programSession.channel_id"
            :options="channelTypeOptions"
            :reduce="(val) => val.id"
            label="name"
            :vuelidate-instance="v$.programSession.channel_id"
            :clearable="false"
            data-testid="program-session-category-dropdown"
            @update:modelValue="changeSessionStatus"
          >
            <template #fieldLabel>
              <label class="w-full common-label-text mt-4">
                <span class="text-red-100 pr-2">*</span>
                Program Session Category
              </label>
            </template>
          </v-select-caret>
          <CheckboxSelect
            v-model="programSession.session_types"
            :vuelidate-instance="v$.programSession.session_types"
            outer-classes="col-span-2"
            :options="sessionTypes"
            :multiple="true"
            :deselect-from-dropdown="true"
            :close-on-select="false"
            placeholder="Pick a value"
            label="name"
            data-testid="program-session-type-dropdown"
          >
            <template #fieldLabel>
              <label class="common-label-text"><span class="text-red-100 pr-2">**</span>Program Session Type</label>
            </template>
          </CheckboxSelect>
          <!-- The global-libraries feature flag is going to be cleaned up soon, remove this once it's done. For now, since we're transfering the
          marketplace toggle to the PromotionalSettings we will only render this toggle when global-libraries is serving true and enable-share-options is false -->
          <div v-if="hasGlobalLibrariesEnabled && !hasEnableShareOptionsEnabled" class="col-span-full -my-2">
            <div class="flex flex-col mt-2 relative container-div">
              <label class="block text-sm font-semibold text-gray-700">Available in Marketplace</label>
              <div class="flex flex-row items-center mt-2 -my-2">
                <BaseToggle
                  v-model="programSession.marketplace"
                  label-after="true"
                  width="w-10"
                  data-testid="session-marketplace-toggle"
                />
                <img
                  v-if="programSession.marketplace"
                  src="/images/icon-marketplace.svg"
                  class="inset-0 ml-2 mb-6 h-6"
                  data-testid="globe-icon"
                />
              </div>
            </div>
          </div>
          <SearchableSelect
            v-if="sessionTypeCustomStudy || sessionTypeCustomFacultyLed"
            v-model="programSession.sponsoring_institutions.entity"
            :clear-search-on-blur="() => false"
            :deselect-from-dropdown="true"
            :loading="entitiesOptionsAllLoading"
            :options="entitiesOptionsAll.items"
            :vuelidate-instance="v$.programSession.sponsoring_institutions.entity"
            placeholder="Sponsoring Institution"
            class="col-span-2"
            label="name"
            @search="fetchEntitiesOptionsAll"
          >
            <template #fieldLabel>
              <label class="common-label-text">Sponsoring Institution</label>
            </template>
          </SearchableSelect>
          <SearchableSelect
            v-if="sessionTypeCoreStudy || sessionTypeCustomStudy || sessionTypeCustomFacultyLed || sessionTypeExchange"
            v-model="programSession.session_hosting_institutions[0].entity"
            :clear-search-on-blur="() => false"
            :deselect-from-dropdown="true"
            :loading="entitiesOptionsAllLoading"
            :options="entitiesOptionsAll.items"
            :vuelidate-instance="
              vuelidateCollectionHelper(v$.programSession.session_hosting_institutions, 0, 'entity')
            "
            placeholder="Host Institution"
            class="col-span-2"
            label="name"
            @search="fetchEntitiesOptionsAll"
          >
            <template #fieldLabel>
              <label class="common-label-text" data-testid="host-institution">
                Host Institution
              </label>
            </template>
          </SearchableSelect>
          <BasicTextInput
            v-if="typeStudy || sessionTypeAdoptedCustom || sessionTypeExchange"
            v-model="programSession.host_institution_program_name"
            outer-classes="col-span-2"
            field-label="Host Institution Program Name"
          />
          <BasicTextInput
            v-if="sessionTypeCoreStudy && sessionEnableProductLineId"
            v-model="programSession.session_hosting_institutions[0].product_line_id"
            outer-classes="col-span-2"
          >
            <template #fieldLabel>
              <label class="common-label-text">Product Line</label>
            </template></BasicTextInput
          >
          <SearchableSelect
            v-if="ownerIsApi"
            v-model="programSession.financial_product_line_id"
            :clear-search-on-blur="() => false"
            :deselect-from-dropdown="true"
            :loading="isLoadingFinancialLine"
            :options="financialLineOptions"
            :vuelidate-instance="v$.programSession.financial_product_line_id"
            :reduce="(option) => option.id"
            :disabled="programSession.financial_data_locked"
            placeholder="Financial Product Line"
            label="name"
          >
            <template #fieldLabel>
              <label class="common-label-text" data-testid="financial-product-line">
                <span class="text-red-100 pr-2">**</span>Financial Product Line
              </label>
            </template>
          </SearchableSelect>

          <SearchableSelect
            v-if="ownerIsApi"
            v-model="programSession.financial_product_type_id"
            :clear-search-on-blur="() => false"
            :deselect-from-dropdown="true"
            :loading="isLoadingFinancialType"
            :options="financialTypesOptions"
            :vuelidate-instance="v$.programSession.financial_product_type_id"
            :reduce="(option) => option.id"
            :disabled="programSession.financial_data_locked"
            placeholder="Financial Product Type"
            label="name"
          >
            <template #fieldLabel>
              <label class="common-label-text" data-testid="financial-product-line">
                <span class="text-red-100 pr-2">**</span>Financial Product Type
              </label>
            </template>
          </SearchableSelect>

          <CalendarInput
            placeholder="Select date"
            name="date"
            :config="config"
            :vuelidate-instance="v$.programSession.academic_start_date"
            :model-value="programSession.academic_start_date || null"
            @onChange="
              (date) =>
                (programSession.academic_start_date = date && date[0] ? format(new Date(date[0]), 'yyyy-MM-dd') : '')
            "
          >
            <template #fieldLabel>
              <label class="common-label-text"><span class="text-red-100 pr-2">**</span>Start Date</label>
            </template>
          </CalendarInput>
          <CalendarInput
            placeholder="Select date"
            name="date"
            :config="config"
            :vuelidate-instance="v$.programSession.academic_end_date"
            :model-value="programSession.academic_end_date || null"
            @onChange="
              (date) =>
                (programSession.academic_end_date = date && date[0] ? format(new Date(date[0]), 'yyyy-MM-dd') : '')
            "
          >
            <template #fieldLabel>
              <label class="common-label-text"><span class="text-red-100 pr-2">**</span>End Date</label>
            </template>
          </CalendarInput>
          <BasicCheckbox
            v-model="programSession.academic_start_date_finalized"
            class="my-auto"
            :vuelidate-instance="v$.programSession.academic_start_date_finalized"
            field-label="Start Date is final"
          />
          <BasicCheckbox
            v-model="programSession.academic_end_date_finalized"
            class="my-auto"
            :vuelidate-instance="v$.programSession.academic_end_date_finalized"
            field-label="End Date is final"
          />
          <BasicTextInput
            v-model="programSession.duration"
            field-label="Duration"
            type="number"
            min="1"
            :disabled="disablePriceDuration"
            :vuelidate-instance="v$.programSession?.duration"
          >
            <template #fieldLabel>
              <p class="common-label-text">
                <span class="text-red-100 pr-2">**</span>
                Price Duration
                <Tooltip :message="DURATION_TOOLTIP_MESSAGE" />
              </p>
            </template>
          </BasicTextInput>
          <div v-if="programSession?.academic_end_date && programSession?.academic_start_date">
            <p class="common-label-text">Calculated Duration</p>
            <p data-testid="session-academic-duration">
              {{ calculatedAcademicDuration }}
            </p>
          </div>
          <BasicCheckbox
            v-model="programSession.visa_required"
            class="my-auto"
            :vuelidate-instance="v$.programSession.visa_required"
            field-label="Program requires a visa"
          />
          <CalendarInput
            v-if="!hasConfigurableRegistrationEnabled"
            placeholder="Select date"
            name="date"
            :config="config"
            :vuelidate-instance="v$.programSession.application_deadline"
            :model-value="programSession.application_deadline || null"
            @onChange="
              (date) =>
                (programSession.application_deadline = date && date[0] ? format(new Date(date[0]), 'yyyy-MM-dd') : '')
            "
          >
            <template #fieldLabel>
              <label class="common-label-text"><span class="text-red-100 pr-2">**</span>Applications Deadline</label>
            </template>
          </CalendarInput>
          <CalendarInput
            v-if="!hasMyStudentsToDosEnabled"
            placeholder="Select date"
            name="date"
            :config="config"
            :vuelidate-instance="v$.programSession.enrollment_deadline"
            :model-value="programSession.enrollment_deadline || null"
            @onChange="
              (date) =>
                (programSession.enrollment_deadline = date && date[0] ? format(new Date(date[0]), 'yyyy-MM-dd') : '')
            "
          >
            <template #fieldLabel>
              <label class="common-label-text"
                ><span class="text-red-100 pr-2">**</span>Enrollment/Forms Deadline</label
              >
            </template>
          </CalendarInput>
          <!--
            // TODO: Check with product if and when this should be activated
            <CalendarInput
            placeholder="Select date"
            name="date"
            field-label="Final Payment Deadline"
            :config="config"
            :vuelidate-instance="v$.programSession.final_payment_deadline"
            :model-value="programSession.final_payment_deadline || null"
            @onChange="
              (date) =>
                (programSession.final_payment_deadline =
                  date && date[0]
                    ? format(new Date(date[0]), 'yyyy-MM-dd')
                    : '')
            "
          >
          </CalendarInput> -->
          <TagsInput
            v-model="programSession.tags"
            field-label="Tags"
            class="col-span-2"
            :vuelidate-instance="v$.programSession.tags"
            label="name"
            :options="specificCategoryTags"
          />

          <ProgramLeaders v-model="programSession" :vuelidate-instance="v$.programSession.session_profiles" />

          <ProgramLocationsSection
            v-model="programSession"
            :disable-price-duration="disablePriceDuration"
            class="col-span-2"
          />
          <ExpandingSection field-label="Terms" class="col-span-2">
            <template #content>
              <div class="grid grid-cols-2 gap-x-24 gap-y-4">
                <BasicSwitch field-label="No designated terms and dates" @update:modelValue="disableTermsAndDates" />
                <v-select-caret
                  v-model="programSession.academic_year"
                  outer-classes="col-span-2"
                  :options="academicYearOptions"
                  label="name"
                  :reduce="(val) => val.id"
                  :vuelidate-instance="v$.programSession.academic_year"
                  :disabled="terms_and_dates_disabled"
                >
                  <template #fieldLabel>
                    <label class="common-label-text"><span class="text-red-100 pr-2">**</span>Academic Year</label>
                  </template>
                </v-select-caret>
                <CheckboxSelect
                  v-model="programSession.terms"
                  :vuelidate-instance="v$.programSession.terms"
                  outer-classes="col-span-2"
                  :options="getTermsData"
                  :multiple="true"
                  :deselect-from-dropdown="true"
                  :close-on-select="false"
                  placeholder="Pick a value"
                  label="name"
                  :disabled="terms_and_dates_disabled"
                >
                  <template #fieldLabel>
                    <label class="common-label-text"><span class="text-red-100 pr-2">**</span>Terms</label>
                  </template>
                </CheckboxSelect>
              </div>
            </template>
          </ExpandingSection>
          <div class="flex justify-between items-center my-4 col-span-2">
            <h1 class="text-xl font-medium leading-6 text-gray-900">
              Participants
            </h1>
          </div>
          <div class="col-span-2">
            <ParticipantsSection
              v-model="programSession"
              :can-edit-configurable-fees="canEditConfigurableFees"
              :owner-entity-id="programSessionEntityId"
            />
          </div>
          <div class="flex justify-between items-center mt-4 mb-2 col-span-2">
            <h1 class="text-xl font-medium leading-6 text-gray-900">
              Components
            </h1>
          </div>
          <Academics v-model="programSession" :is-session-owner="isSessionOwner" class="col-span-2" />
          <TodosSection
            v-if="hasMyStudentsToDosEnabled"
            v-model="sessionTodos"
            :loading="sessionTodosLoading"
            :has-edit-permissions="todoSessionsCanEdit"
            class="col-span-2"
          />
          <Internships v-model="programSession" :is-session-owner="isSessionOwner" class="col-span-2" />
          <Housing v-model="programSession" class="col-span-2" />
          <ExcursionsAndEvents v-model="programSession" :is-session-owner="isSessionOwner" class="col-span-2" />
          <ExpandingSection field-label="Insurance" class="col-span-2">
            <template #content>
              <div class="grid grid-cols-1 gap-x-24 gap-y-4">
                <BasicSwitch v-model="programSession.designated_insurance" field-label="No designated insurance" />
              </div>
            </template>
          </ExpandingSection>
          <AdditionalProgramSessionFees v-model="programSession" class="col-span-2" />
          <ProductsAvailableAddOn v-model="programSession" class="col-span-2" />
          <PromotionalSettings v-model="programSession" class="col-span-2" />
          <ExpandingSection field-label="Parent Program Session" class="col-span-2" :expanded-by-default="false">
            <template #content>
              <SearchableSelect
                v-model="programSession.parent_id"
                :vuelidate-instance="v$.programSession.parent_id"
                :reduce="(val) => val.id"
                placeholder="Search and add parent program session"
                class="col-span-2 mt-4"
                label="name"
                :loading="parentOptionsLoading"
                :options="
                  programSession?.parent
                    ? [...(parentOptions?.items || []), programSession.parent]
                    : parentOptions?.items
                "
                :deselect-from-dropdown="true"
                :clear-search-on-blur="() => false"
                @search="fetchParentOptions"
              >
              </SearchableSelect>
            </template>
          </ExpandingSection>
          <ProgramSessionSpecificAdvisingInstructions v-model="programSession" class="col-span-2"/>
        </div>
      </div>

      <div class="col-span-2 space-y-2">
        <DisabledProgramSessionFeesSummary v-if="anyDirty" />
        <ProgramSessionOrdersSummary
          v-if="!!sessionSummaryData?.data?.summary.length"
          :summary="sessionSummaryData?.data?.summary"
        />
        <ProgramSessionFeesSummary
          v-if="!anyDirty"
          :product-prices="sessionPricingSummaryForFees"
          :is-session-owner="isSessionOwner"
        />
      </div>
    </div>
  </div>
</template>
